import {
  cropToPortalCrop,
  DisplayCrop,
  getCrops,
  SortedCrops,
} from "portal/utils/crops";
import { portalEndpoints } from "portal/state/portalApi";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";

interface UseCropsOptions {
  // Pass a robot serial to get crops for that robot, or
  // omit serial to get global crops.
  serial?: string;
  skip?: boolean;
}

interface UseCropsResult {
  sortedCrops: SortedCrops | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export const useSortedCrops = ({
  serial,
  skip = false,
}: UseCropsOptions): UseCropsResult => {
  const { i18n, t } = useTranslation();
  const language = portalLanguageOrDefault(i18n.language);

  // for global queries (no serial), request all crops
  const {
    data: allCrops,
    isFetching: isAllCropsFetching,
    isError: isAllCropsError,
    error: allCropsError,
  } = useQueryPopups(
    portalEndpoints.listCrops.useQuery(skip ? skipToken : undefined)
  );

  // for robot queries (with serial), request configs and robot crops
  const {
    data: config,
    isFetching: isConfigFetching,
    isError: isConfigError,
    error: configError,
  } = useQueryPopups(
    portalEndpoints.getConfig.useQuery(
      skip || !serial ? skipToken : { serial, noCache: true }
    ),
    { errorVariant: "warning" }
  );

  const sortedCrops = useMemo<SortedCrops | undefined>(() => {
    if (serial) {
      // crops for single robot
      if (!config) {
        return;
      }
      return getCrops(serial, config.config, allCrops, language, t);
    }

    // global crops
    if (!allCrops) {
      return;
    }
    const archived: DisplayCrop[] = [];
    const all: DisplayCrop[] = [];
    for (const crop of Object.values(allCrops)) {
      const configCrop = cropToPortalCrop(crop, language);
      if (crop.archived) {
        archived.push(configCrop);
      }
      all.push(configCrop);
    }
    return {
      all,
      enabled: all,
      disabled: [],
      archived,
      pinnedAndEnabledCrops: 0,
    };
  }, [serial, config, allCrops, language, t]);

  const isFetching = isConfigFetching || isAllCropsFetching;
  const isError = isConfigError || isAllCropsError;
  const error = configError ?? allCropsError;
  return useMemo(
    () => ({
      sortedCrops,
      isFetching,
      isError,
      error,
    }),
    [sortedCrops, isFetching, isError, error]
  );
};
